/**
 * Sales Chat Examples - XML-structured borderline calibration
 * Phase 2.2: Examples for chat sales quality scoring
 */

export const SALES_CHAT_EXAMPLES = `
<examples category="sales-chat">

  <!-- Message Brevity: Too Long (40) vs Optimal (85) -->

  <example category="message_brevity" score="40" tier="needs_improvement">
    <dialogue>
      <agent speaker="agent" timestamp="10:15">
        Hi there! Thank you so much for reaching out to us today. We really appreciate your interest in our product. I'd love to tell you all about our amazing features including our advanced reporting dashboard which gives you real-time insights into your business metrics, our automated workflow engine that can save you hours every week, our integrations with over 50 popular tools including Salesforce, HubSpot, Slack, Google Workspace and many more, plus our 24/7 customer support team that's always ready to help you with any questions you might have. We also offer a 14-day free trial so you can test everything out before committing. Does this sound interesting to you?
      </agent>
    </dialogue>
    <analysis>
      <word_count>125 words</word_count>
      <optimal_range>20-50 words</optimal_range>
      <issues>
        - Information overload (too many features at once)
        - No white space for customer to respond
        - Wall of text difficult to read on mobile
        - Question buried at end
      </issues>
      <calculation>125 words / 50 optimal × 100 = 250%, score = 40/100</calculation>
    </analysis>
    <improvement>
      Break into 2-3 shorter messages:
      "Hi! Thanks for your interest 👋"
      "We help businesses automate workflows and save time. What brings you here today?"
    </improvement>
  </example>

  <example category="message_brevity" score="85" tier="proficient">
    <dialogue>
      <agent speaker="agent" timestamp="10:15">Hi! Thanks for reaching out 👋</agent>
      <agent speaker="agent" timestamp="10:16">We help businesses automate workflows and save hours each week.</agent>
      <agent speaker="agent" timestamp="10:17">What brings you here today?</agent>
    </dialogue>
    <analysis>
      <word_count>Message 1: 5 words, Message 2: 11 words, Message 3: 5 words</word_count>
      <optimal_range>20-50 words total, 3-15 per message</optimal_range>
      <strengths>
        - Bite-sized messages
        - Easy to read on mobile
        - Quick greeting + value prop + question
        - Space for customer to respond
      </strengths>
      <calculation>21 total words, 3 messages = optimal, score = 85/100</calculation>
    </analysis>
    <threshold_note>
      Score 70+ requires:
      - Messages under 50 words
      - 2-4 message chunks (not one wall of text)
      - Clear question at end
    </threshold_note>
  </example>

  <borderline_calibration score_A="60" score_B="85">
    <question>What separates Developing brevity (60) from Proficient (85)?</question>
    <answer>
      - 85+: Under 50 words total, split into 2-4 messages
      - 60: 50-80 words, possibly one long message
      - 85+: Easy to scan on mobile
      - 60: Requires scrolling or "read more"
      - 85+: One clear question, 60: multiple questions or buried question
    </answer>
  </borderline_calibration>

  <!-- Response Time: Slow (50) vs Fast (90) -->

  <example category="response_time" score="50" tier="developing">
    <dialogue>
      <customer speaker="customer" timestamp="10:15">How much does your Pro plan cost?</customer>
      <agent speaker="agent" timestamp="10:22">[7 minutes later] It's $99/month</agent>
    </dialogue>
    <analysis>
      <response_time>7 minutes</response_time>
      <optimal_range>Under 2 minutes</optimal_range>
      <impact>
        Customer may have left chat.
        Shows inattention or high queue volume.
        Kills momentum.
      </impact>
      <calculation>7 min / 2 min threshold = 350%, score = 50/100</calculation>
    </analysis>
    <improvement>
      Set expectation if delayed:
      "One moment while I pull up pricing 👍" [within 30 seconds]
    </improvement>
  </example>

  <example category="response_time" score="90" tier="exceptional">
    <dialogue>
      <customer speaker="customer" timestamp="10:15">How much does your Pro plan cost?</customer>
      <agent speaker="agent" timestamp="10:15">[45 seconds later] Pro plan is $99/month</agent>
      <agent speaker="agent" timestamp="10:16">Includes unlimited users + priority support</agent>
      <agent speaker="agent" timestamp="10:16">Want to see a quick demo?</agent>
    </dialogue>
    <analysis>
      <response_time>45 seconds for first message</response_time>
      <optimal_range>Under 2 minutes, ideally under 1 minute</optimal_range>
      <strengths>
        - Fast response maintains momentum
        - Added value (what's included)
        - Next step (demo offer)
      </strengths>
      <calculation>45 sec &lt; 1 min = excellent, score = 90/100</calculation>
    </analysis>
    <threshold_note>
      Score 70+ requires:
      - Initial response under 2 minutes
      - Follow-up responses under 1 minute
      - If delayed, acknowledge within 30 seconds
    </threshold_note>
  </example>

  <borderline_calibration score_A="60" score_B="90">
    <question>What separates Slow response (60) from Fast (90)?</question>
    <answer>
      - 90+: Under 1 minute for straightforward questions
      - 60: 2-5 minutes
      - 90+: Maintains conversation momentum
      - 60: Feels like agent is distracted
      - Gray area (70-80): 1-2 minutes, or delayed but acknowledged quickly
    </answer>
  </borderline_calibration>

  <!-- Link Sharing: Weak (35) vs Strong (95) -->

  <example category="link_sharing" score="35" tier="needs_improvement">
    <dialogue>
      <customer speaker="customer" timestamp="11:20">Can I see a demo?</customer>
      <agent speaker="agent" timestamp="11:22">
        Sure, go to our website and click on "Book Demo" and fill out the form
      </agent>
    </dialogue>
    <analysis>
      <link_provided>No (only instructions)</link_provided>
      <friction>High (customer must navigate, find page, fill form)</friction>
      <issues>
        - No direct link
        - Multiple steps required
        - Customer may abandon
      </issues>
      <calculation>No link = 0, instructions = 35/100</calculation>
    </analysis>
    <improvement>
      "Here's my calendar link: [calendly.com/john]"
      "Pick a time that works for you 📅"
    </improvement>
  </example>

  <example category="link_sharing" score="95" tier="exceptional">
    <dialogue>
      <customer speaker="customer" timestamp="11:20">Can I see a demo?</customer>
      <agent speaker="agent" timestamp="11:22">Absolutely! 🎯</agent>
      <agent speaker="agent" timestamp="11:23">Here's my calendar: calendly.com/john-acme</agent>
      <agent speaker="agent" timestamp="11:24">Pick any 30-min slot this week. I'll walk you through how we can save your team 10+ hours/week</agent>
    </dialogue>
    <analysis>
      <link_provided>Yes (Calendly)</link_provided>
      <friction>Low (one click to book)</friction>
      <context_provided>Yes (30-min, this week, value prop)</context_provided>
      <strengths>
        - Direct clickable link
        - Set expectations (duration, timing)
        - Reinforced value (10+ hours saved)
      </strengths>
      <calculation>Link + context + value = 95/100</calculation>
    </analysis>
    <threshold_note>
      Score 70+ requires:
      - Direct clickable link (not "go to our website")
      - Context (duration, timing)
      - Optional: Value reinforcement
    </threshold_note>
  </example>

  <borderline_calibration score_A="35" score_B="95">
    <question>What separates Weak link sharing (35) from Strong (95)?</question>
    <answer>
      - 95+: Direct clickable link with context
      - 35: Instructions without link
      - 95+: Low friction (one click)
      - 35: High friction (navigate → find → fill form)
      - 95+: Reinforces value, 35: no value reminder
      - Gray area (60-70): Link provided but no context
    </answer>
  </borderline_calibration>

  <!-- Closing Technique (Chat-specific) -->

  <example category="closing_technique" score="45" tier="needs_improvement">
    <dialogue>
      <agent speaker="agent" timestamp="12:30">
        Let me know if you have any questions!
      </agent>
    </dialogue>
    <analysis>
      <technique_used>passive_close</technique_used>
      <cta_provided>No</cta_provided>
      <next_step_clear>No</next_step_clear>
      <issues>
        - Puts burden on customer
        - No clear next action
        - Conversation may die
      </issues>
      <calculation>Passive close = 45/100</calculation>
    </analysis>
    <improvement>
      "Ready to try it? Here's a 14-day free trial link: [app.acme.com/trial]"
      "No credit card needed 👍"
    </improvement>
  </example>

  <example category="closing_technique" score="90" tier="exceptional">
    <dialogue>
      <agent speaker="agent" timestamp="12:30">Based on your team size, Pro plan will save you ~$2K/month in time</agent>
      <agent speaker="agent" timestamp="12:31">Want to start a free trial? Takes 2 minutes: app.acme.com/trial</agent>
      <agent speaker="agent" timestamp="12:32">I can help you set it up right now if you'd like 🚀</agent>
    </dialogue>
    <analysis>
      <technique_used>direct_ask_with_link</technique_used>
      <cta_provided>Yes (trial link)</cta_provided>
      <value_referenced>Yes ($2K/month savings)</value_referenced>
      <friction_reduced>Yes (2 minutes setup)</friction_reduced>
      <assistance_offered>Yes (help right now)</assistance_offered>
      <calculation>Value + Link + Assistance = 90/100</calculation>
    </analysis>
    <threshold_note>
      Score 70+ requires:
      - Clear CTA with direct link
      - Reference to value/ROI
      - Low friction messaging ("2 minutes", "no credit card")
    </threshold_note>
  </example>

  <example category="closing_technique" score="100" tier="exceptional">
    <dialogue>
      <agent speaker="agent" timestamp="12:30">Perfect! I'll set up your account now</agent>
      <agent speaker="agent" timestamp="12:31">Best email to use?</agent>
    </dialogue>
    <analysis>
      <technique_used>assumptive_close</technique_used>
      <cta_provided>Implied (setting up account)</cta_provided>
      <confidence>High (assumes yes)</confidence>
      <reasoning>
        Assumes sale is done, moves to logistics.
        No hesitation, no "if you want" language.
        Natural progression.
      </reasoning>
      <calculation>Assumptive close = 100/100</calculation>
    </analysis>
  </example>

  <borderline_calibration score_A="45" score_B="90">
    <question>What separates Passive close (45) from Strong CTA (90)?</question>
    <answer>
      - 90+: Direct link + value reference + clear next step
      - 45: "Let me know" or "any questions?" (no CTA)
      - 90+: Low friction ("2 minutes", "no credit card")
      - 45: No friction reduction messaging
      - 90+: Offers help immediately, 45: waits for customer
      - Gray area (60-70): CTA provided but no value reference or link
    </answer>
  </borderline_calibration>

  <!-- Emoji Usage: None (60) vs Balanced (80) vs Overuse (40) -->

  <example category="emoji_usage" score="60" tier="developing">
    <dialogue>
      <agent speaker="agent" timestamp="13:15">Hi. Thanks for your message. How can I help you today.</agent>
    </dialogue>
    <analysis>
      <emoji_count>0</emoji_count>
      <tone>Formal, stiff</tone>
      <issues>
        - Too formal for chat medium
        - Lacks warmth
        - Doesn't match customer expectations for chat
      </issues>
      <calculation>0 emojis in greeting = 60/100</calculation>
    </analysis>
    <improvement>
      "Hi! Thanks for reaching out 👋"
      "How can I help you today?"
    </improvement>
  </example>

  <example category="emoji_usage" score="80" tier="proficient">
    <dialogue>
      <agent speaker="agent" timestamp="13:15">Hi! Thanks for reaching out 👋</agent>
      <agent speaker="agent" timestamp="13:16">I'd love to show you how we can help 🎯</agent>
      <agent speaker="agent" timestamp="13:17">Here's my calendar: [link] 📅</agent>
    </dialogue>
    <analysis>
      <emoji_count>3 in 3 messages</emoji_count>
      <tone>Friendly, professional</tone>
      <emoji_placement>End of message (not disruptive)</emoji_placement>
      <emoji_relevance>Contextual (wave, target, calendar)</emoji_relevance>
      <calculation>1 emoji per message, contextual = 80/100</calculation>
    </analysis>
    <threshold_note>
      Score 70+ requires:
      - 1-2 emojis per conversation
      - Placed at end of message
      - Contextually relevant
      - Not overused
    </threshold_note>
  </example>

  <example category="emoji_usage" score="40" tier="needs_improvement">
    <dialogue>
      <agent speaker="agent" timestamp="13:15">Hi!!! 👋😊🎉</agent>
      <agent speaker="agent" timestamp="13:16">Thanks for reaching out!!! 🙌💯✨</agent>
      <agent speaker="agent" timestamp="13:17">We're SO excited to help you!!! 🚀🎯💪</agent>
    </dialogue>
    <analysis>
      <emoji_count>9 in 3 messages</emoji_count>
      <tone>Overly enthusiastic, unprofessional</tone>
      <issues>
        - Too many emojis (distracting)
        - Multiple exclamation marks
        - Feels spammy/desperate
      </issues>
      <calculation>3+ emojis per message = overuse = 40/100</calculation>
    </analysis>
    <improvement>
      Use 1 emoji per message, max 2-3 per conversation.
    </improvement>
  </example>

  <borderline_calibration score_A="60" score_B="80">
    <question>What separates No emojis (60) from Balanced (80)?</question>
    <answer>
      - 80: 1-2 contextual emojis per conversation
      - 60: Zero emojis (too formal for chat)
      - 80: Professional + friendly balance
      - 60: Stiff, email-like tone
      - Overuse (40): 3+ emojis per message (unprofessional)
    </answer>
  </borderline_calibration>

  <!-- Comprehensive Chat Example -->

  <comprehensive_example>
    <chat_summary>
      Complete sales chat showing brevity, speed, links, and closing
    </chat_summary>
    <dialogue>
      <customer timestamp="14:00">Hi, interested in your Pro plan</customer>
      <agent timestamp="14:00">[30 seconds] Hey! Great to hear 👋</agent>
      <agent timestamp="14:01">Pro plan is perfect for teams 5-20 people</agent>
      <agent timestamp="14:01">$99/month, includes unlimited users + priority support</agent>
      <customer timestamp="14:02">How long is setup?</customer>
      <agent timestamp="14:02">[45 seconds] Super quick! About 5 minutes</agent>
      <agent timestamp="14:03">Want to start a free trial? No credit card needed: app.acme.com/trial</agent>
      <agent timestamp="14:03">I can walk you through setup right now if you'd like 🚀</agent>
      <customer timestamp="14:05">Sure, let's do it</customer>
      <agent timestamp="14:05">Perfect! What's your work email?</agent>
    </dialogue>
    <comprehensive_analysis>
      <message_brevity>
        <score>85</score>
        <reasoning>All messages under 15 words, easy to read</reasoning>
      </message_brevity>
      <response_time>
        <score>95</score>
        <reasoning>30-45 second responses, maintains momentum</reasoning>
      </response_time>
      <link_sharing>
        <score>90</score>
        <reasoning>Direct trial link with low-friction messaging</reasoning>
      </link_sharing>
      <closing_technique>
        <score>95</score>
        <reasoning>Assumptive close after trial offer, moved to logistics</reasoning>
      </closing_technique>
      <emoji_usage>
        <score>80</score>
        <reasoning>2 contextual emojis (wave, rocket), not overused</reasoning>
      </emoji_usage>
      <overall_quality>89</overall_quality>
    </comprehensive_analysis>
  </comprehensive_example>

</examples>
`;
